import { cn } from "@/lib/utils";
import {
  formatCents,
  formatCentsCompact,
  formatDurationMs,
  formatMoneyRange,
  formatPretensao,
  formatUsd,
  Moeda,
  parseMoeda,
  toCents,
  type MoneyUnit,
} from "@/lib/format";
import { EmptyValue } from "./EmptyValue";

interface MoneyProps {
  value: number | null | undefined;
  /** Unidade de armazenamento. Verba e finance: centavos; valor_projeto: reais. */
  unit: MoneyUnit;
  /** O que vem da coluna `moeda`. Sem ela, real. */
  moeda?: Moeda | string | null;
  compact?: boolean;
  className?: string;
}

export function Money({ value, unit, moeda, compact = false, className }: MoneyProps) {
  if (value == null) return <EmptyValue />;
  const cents = toCents(value, unit);
  const currency = parseMoeda(moeda);
  return (
    <span className={cn("tabular-nums", className)} title={compact ? formatCents(cents, currency) : undefined}>
      {compact ? formatCentsCompact(cents, currency) : formatCents(cents, currency)}
    </span>
  );
}

interface MoneyRangeProps {
  min: number | null | undefined;
  max: number | null | undefined;
  unit: MoneyUnit;
  /** O que vem da coluna `moeda`. Sem ela, real. */
  moeda?: Moeda | string | null;
  compact?: boolean;
  /** Ex.: "/mês". */
  suffix?: string;
  className?: string;
}

export function MoneyRange({ min, max, unit, moeda, compact = true, suffix, className }: MoneyRangeProps) {
  const text = formatMoneyRange(min, max, { unit, compact, moeda: parseMoeda(moeda) });
  if (!text) return <EmptyValue />;
  return (
    <span className={cn("tabular-nums", className)}>
      {text}
      {suffix && <span className="ml-0.5 font-normal text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/** Pretensão salarial (texto livre no banco). */
export function Pretensao({ value, className }: { value: string | number | null | undefined; className?: string }) {
  const text = formatPretensao(value);
  if (!text) return <EmptyValue label="Não informada" />;
  return <span className={cn("tabular-nums", className)}>{text}</span>;
}

/** Custo em dólar ESTIMADO (tabela de preços x uso), nunca valor faturado. */
export function UsdEstimate({ value, className }: { value: number | null | undefined; className?: string }) {
  if (value == null) return <EmptyValue />;
  return (
    <span className={cn("tabular-nums", className)} title="Estimativa pela tabela de preços, não é o valor faturado">
      {formatUsd(value)}
    </span>
  );
}

/** Duração de uma execução: "850 ms", "3 min 5 s". */
export function Duration({ milliseconds, className }: { milliseconds: number | null | undefined; className?: string }) {
  if (milliseconds == null) return <EmptyValue />;
  return <span className={cn("tabular-nums", className)}>{formatDurationMs(milliseconds)}</span>;
}
